import { Component, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type ItemCategory = 'Stationery' | 'Lab Equipment' | 'IT Equipment' | 'Furniture' | 'Sports' | 'Library' | 'Maintenance' | 'Canteen' | 'Others';
export type ItemUnit = 'Pcs' | 'Box' | 'Kg' | 'Ltr' | 'Pack' | 'Ream' | 'Dozen' | 'Set' | 'Pair' | 'Roll';
export type StockStatus = 'In Stock' | 'Low Stock' | 'Out of Stock';
export type POStatus = 'Draft' | 'Sent' | 'Partial' | 'Received' | 'Cancelled';
export type IssueStatus = 'Pending' | 'Approved' | 'Issued' | 'Rejected';
export type MovementType = 'Purchase' | 'Issue' | 'Return' | 'Adjustment' | 'Opening';
export type InvTab = 'dashboard' | 'items' | 'stock' | 'purchases' | 'issues' | 'suppliers' | 'reports';
export type ReportView = 'valuation' | 'lowstock' | 'movements' | 'category';

// ──────────────────────────────────────────────────────────
// INTERFACES
// ──────────────────────────────────────────────────────────
export interface InvItem {
  id: number; item_sku: string; item_name: string; category: ItemCategory; unit: ItemUnit;
  current_stock: number; min_stock: number; max_stock: number;
  cost_price: number; supplier_id: number; supplier_name: string;
  storage_location: string; is_active: boolean; description: string; last_purchase_date: string;
}
export interface Supplier {
  id: number; sup_name: string; contact_person: string; email: string; phone: string;
  address: string; city: string; gst_no: string; payment_terms: string; is_active: boolean;
}
export interface POItem {
  item_id: number; item_name: string; item_sku: string; unit: string;
  quantity: number; received_qty: number; unit_price: number; total: number;
}
export interface PurchaseOrder {
  id: number; po_no: string; supplier_id: number; supplier_name: string;
  order_date: string; expected_date: string; received_date: string;
  items: POItem[]; total_amount: number; status: POStatus; notes: string;
  created_by: string; created_at: string;
}
export interface IssueItem {
  item_id: number; item_name: string; item_sku: string; unit: string;
  requested_qty: number; issued_qty: number;
}
export interface StockIssue {
  id: number; issue_no: string; department: string; requested_by: string;
  request_date: string; issue_date: string; items: IssueItem[];
  status: IssueStatus; purpose: string; approved_by: string; remarks: string;
}
export interface StockMovement {
  id: number; item_id: number; item_name: string; item_sku: string;
  mov_type: MovementType; quantity: number; reference: string;
  mov_date: string; balance_after: number; notes: string;
}

// ──────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────
@Component({
  selector: 'app-inventory',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './inventory.html',
  styleUrl: './inventory.scss',
})
export class Inventory {
  dataService = inject(DataService);
  loading = false;
  user: any = null;

  // ── Tabs ──────────────────────────────────────────────────
  activeTab = signal<InvTab>('dashboard');
  reportView = signal<ReportView>('valuation');
  toast = signal('');

  // ── Modals ────────────────────────────────────────────────
  showItemModal = signal(false);
  showSupplierModal = signal(false);
  showPOModal = signal(false);
  showIssueModal = signal(false);
  showAddStockModal = signal(false);
  viewingPO = signal<PurchaseOrder | null>(null);
  viewingIssue = signal<StockIssue | null>(null);
  editingId = signal<number | null>(null);

  // ── Forms ─────────────────────────────────────────────────
  itemForm: Partial<InvItem> = {};
  supplierForm: Partial<Supplier> = {};
  poForm: { supplier_id: number | null; supplier_name: string; order_date: string; expected_date: string; notes: string; items: Partial<POItem>[]; } =
    { supplier_id: null, supplier_name: '', order_date: '', expected_date: '', notes: '', items: [] };
  issueForm: { department: string; requested_by: string; purpose: string; remarks: string; items: Partial<IssueItem>[]; } =
    { department: '', requested_by: '', purpose: '', remarks: '', items: [] };
  addStockForm: { item_id: number | null; item_name: string; quantity: number; unit_price: number; reference: string; notes: string; } =
    { item_id: null, item_name: '', quantity: 0, unit_price: 0, reference: '', notes: '' };

  itemSubmitted = false; supSubmitted = false; poSubmitted = false;
  issSubmitted = false; stkSubmitted = false;

  // ── Filters ───────────────────────────────────────────────
  itemSearch = signal('');
  itemCategory = signal<ItemCategory | ''>('');
  itemStatus = signal<StockStatus | ''>('');
  stockSearch = signal('');
  poSearch = signal('');
  poStatus = signal<POStatus | ''>('');
  issSearch = signal('');
  issStatus = signal<IssueStatus | ''>('');
  supSearch = signal('');

  // ── Counters ──────────────────────────────────────────────
  private itemCounter = 25;
  private poCounter = 8;
  private issCounter = 8;
  private movCounter = 20;
  private supCounter = 8;

  // ── Data signals ──────────────────────────────────────────
  items = signal<InvItem[]> ([]);                 //(this.buildItems());
  suppliers = signal<Supplier[]> ([]);            //(this.buildSuppliers());
  purchaseOrders = signal<PurchaseOrder[]>([]);   //(this.buildPOs());
  stockIssues = signal<StockIssue[]>([]);         //(this.buildIssues());
  movements = signal<StockMovement[]>([]);        //(this.buildMovements());

  // ── Computed ──────────────────────────────────────────────
  readonly itemStats = computed(() => {
    const its = this.items();
    return {
      total: its.length,
      inStock: its.filter(i => this.stockStatus(i) === 'In Stock').length,
      lowStock: its.filter(i => this.stockStatus(i) === 'Low Stock').length,
      outOfStock: its.filter(i => this.stockStatus(i) === 'Out of Stock').length,
      totalValue: its.reduce((s, i) => s + i.current_stock * i.cost_price, 0),
      categories: [...new Set(its.map(i => i.category))].length,
    };
  });

  readonly poStats = computed(() => {
    const pos = this.purchaseOrders();
    return {
      draft: pos.filter(p => p.status === 'Draft').length,
      pending: pos.filter(p => p.status === 'Sent' || p.status === 'Partial').length,
      received: pos.filter(p => p.status === 'Received').length,
    };
  });

  readonly issueStats = computed(() => {
    const iss = this.stockIssues();
    return {
      pending: iss.filter(i => i.status === 'Pending').length,
      approved: iss.filter(i => i.status === 'Approved').length,
      issued: iss.filter(i => i.status === 'Issued').length,
    };
  });

  readonly filteredItems = computed(() => {
    const q = this.itemSearch().toLowerCase();
    return this.items().filter(i =>
      (!q || [i.item_name, i.item_sku, i.category, i.supplier_name].some(v => v.toLowerCase().includes(q))) &&
      (!this.itemCategory() || i.category === this.itemCategory()) &&
      (!this.itemStatus() || this.stockStatus(i) === this.itemStatus())
    ).sort((a, b) => a.item_name.localeCompare(b.item_name));
  });

  readonly filteredStock = computed(() => {
    const q = this.stockSearch().toLowerCase();
    return this.items()
      .filter(i => !q || [i.item_name, i.item_sku, i.category].some(v => v.toLowerCase().includes(q)))
      .sort((a, b) => {
        const s = (x: InvItem) => this.stockStatus(x) === 'Out of Stock' ? 0 : this.stockStatus(x) === 'Low Stock' ? 1 : 2;
        return s(a) - s(b) || a.item_name.localeCompare(b.item_name);
      });
  });

  readonly filteredPOs = computed(() => {
    const q = this.poSearch().toLowerCase();
    return this.purchaseOrders().filter(p =>
      (!q || [p.po_no, p.supplier_name, p.notes].some(v => v.toLowerCase().includes(q))) &&
      (!this.poStatus() || p.status === this.poStatus())
    ).sort((a, b) => b.order_date.localeCompare(a.order_date));
  });

  readonly filteredIssues = computed(() => {
    const q = this.issSearch().toLowerCase();
    return this.stockIssues().filter(i =>
      (!q || [i.issue_no, i.department, i.requested_by, i.purpose].some(v => v.toLowerCase().includes(q))) &&
      (!this.issStatus() || i.status === this.issStatus())
    ).sort((a, b) => b.request_date.localeCompare(a.request_date));
  });

  readonly filteredSuppliers = computed(() => {
    const q = this.supSearch().toLowerCase();
    return this.suppliers().filter(s =>
      !q || [s.sup_name, s.contact_person, s.city, s.phone].some(v => v.toLowerCase().includes(q))
    );
  });

  readonly lowStockItems = computed(() =>
    this.items().filter(i => this.stockStatus(i) !== 'In Stock').sort((a, b) => a.current_stock - b.current_stock)
  );

  readonly categoryStats = computed(() => {
    const map: Record<string, { count: number; value: number; inStock: number }> = {};
    this.items().forEach(i => {
      if (!map[i.category]) map[i.category] = { count: 0, value: 0, inStock: 0 };
      map[i.category].count++;
      map[i.category].value += i.current_stock * i.cost_price;
      map[i.category].inStock += (this.stockStatus(i) === 'In Stock' ? 1 : 0);
    });
    const total = Object.values(map).reduce((s, v) => s + v.value, 0);
    return Object.entries(map)
      .map(([cat, d]) => ({ cat, ...d, pct: total > 0 ? Math.round((d.value / total) * 100) : 0 }))
      .sort((a, b) => b.value - a.value);
  });

  readonly valuationReport = computed(() =>
    this.items()
      .map(i => ({ ...i, value: i.current_stock * i.cost_price, status: this.stockStatus(i) }))
      .sort((a, b) => b.value - a.value)
  );

  readonly uniqueCategories = computed(() => [...new Set(this.items().map(i => i.category))].sort() as ItemCategory[]);

  // ── Chart data ────────────────────────────────────────────
  readonly trendData = [
    { month: 'Jan', in: 420, out: 185 },
    { month: 'Feb', in: 310, out: 230 },
    { month: 'Mar', in: 560, out: 290 },
    { month: 'Apr', in: 440, out: 200 },
    { month: 'May', in: 670, out: 310 },
    { month: 'Jun', in: 490, out: 275 },
  ];
  private readonly _cH = 130;
  private readonly _cMax = 800;
  readonly barW = 22;

  barY(v: number): number { return 10 + this._cH - Math.round((v / this._cMax) * this._cH); }
  barH(v: number): number { return Math.max(2, Math.round((v / this._cMax) * this._cH)); }
  barX(i: number, isOut: boolean): number { return 50 + i * 82 + (isOut ? this.barW + 4 : 0); }
  monthLblX(i: number): number { return 50 + i * 82 + this.barW + 2; }
  readonly chartBaseY = 140;

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");
    if(erp_rem && !this.user) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
    }

    this.loading = true;
    await this.dataService.getAllRecords('inv_suppliers').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.suppliers.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('inv_items').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.items.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('inv_purchase_orders').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.purchaseOrders.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('inv_stock_issues').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.stockIssues.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('inv_item_movements').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.movements.set(res);
      }
    });

  }

  // Status donut
  statusDonutSegs(): { dash: string; offset: string; color: string; label: string; count: number }[] {
    const s = this.itemStats();
    const total = s.total; if (!total) return [];
    const r = 52, c = 2 * Math.PI * r;
    const segs = [
      { count: s.inStock, color: '#059669', label: 'In Stock' },
      { count: s.lowStock, color: '#f59e0b', label: 'Low Stock' },
      { count: s.outOfStock, color: '#ef4444', label: 'Out of Stock' },
    ];
    let off = 0;
    return segs.map(s => {
      const pct = s.count / total;
      const dash = (pct * c).toFixed(2);
      const gap = (c * (1 - pct)).toFixed(2);
      const seg = { ...s, dash: `${dash} ${gap}`, offset: (-(off * c)).toFixed(2) };
      off += pct;
      return seg;
    });
  }

  catBarW(pct: number): string { return pct + '%'; }

  // ── Item CRUD ─────────────────────────────────────────────
  openItemForm(item?: InvItem): void {
    if (item) { this.itemForm = { ...item }; this.editingId.set(item.id); }
    else {
      this.itemForm = {
        category: 'Stationery', unit: 'Pcs', current_stock: 0, min_stock: 10, max_stock: 100,
        cost_price: 0, supplier_id: 0, supplier_name: '', storage_location: '', is_active: true, description: '',
        last_purchase_date: '',
      };
      this.editingId.set(null);
    }
    this.itemSubmitted = false;
    this.showItemModal.set(true);
  }

  saveItem(): void {
    this.itemSubmitted = true;
    if (!this.itemForm.item_name?.trim() || !this.itemForm.item_sku?.trim()) return;
    const id = this.editingId();

    if (id) {
      this.doUpdate('inv_items', this.itemForm);

      this.items.update(l => l.map(i => i.id === id ? { ...i, ...this.itemForm } as InvItem : i));
    } else {
      this.doSave('inv_items', this.itemForm);

      const n = ++this.itemCounter;
      this.items.update(l => [...l, {
        id: n, item_sku: this.itemForm.item_sku!, item_name: this.itemForm.item_name!,
        category: this.itemForm.category ?? 'Others', unit: this.itemForm.unit ?? 'Pcs',
        current_stock: this.itemForm.current_stock ?? 0, min_stock: this.itemForm.min_stock ?? 10,
        max_stock: this.itemForm.max_stock ?? 100, cost_price: this.itemForm.cost_price ?? 0,
        supplier_id: this.itemForm.supplier_id ?? 0, supplier_name: this.itemForm.supplier_name ?? '',
        storage_location: this.itemForm.storage_location ?? '', is_active: true,
        description: this.itemForm.description ?? '', last_purchase_date: '',
      }]);
    }

    this.showItemModal.set(false);
    this.flash('Item saved.');
  }

  deleteItem(id: number): void {
    if (!confirm('Delete this inventory item?')) return;
    this.items.update(l => l.filter(i => i.id !== id));
    this.flash('Item deleted.');
  }

  // ── Quick Add Stock ────────────────────────────────────────
  openAddStock(item?: InvItem): void {
    this.addStockForm = {
      item_id: item?.id ?? null,
      item_name: item?.item_name ?? '',
      quantity: 0,
      unit_price: item?.cost_price ?? 0,
      reference: '',
      notes: '',
    };
    this.stkSubmitted = false;
    this.showAddStockModal.set(true);
  }

  saveAddStock(): void {
    this.stkSubmitted = true;
    if (!(this.addStockForm.quantity > 0) || !this.addStockForm.item_id) return;

    const id = this.addStockForm.item_id;
    const qty = this.addStockForm.quantity;
    const now = this.isoDate(0);
    let newBal = 0;

    this.items.update(l => l.map(i => {
      if (i.id !== id) return i;
      newBal = i.current_stock + qty;
      return { ...i, current_stock: newBal, last_purchase_date: now, cost_price: this.addStockForm.unit_price || i.cost_price };
    }));

    const item = this.items().find(i => i.id === id);
    this.doUpdate('inv_items', item);

    const movItem: any = {
      id: ++this.movCounter, item_id: id,
      item_name: this.addStockForm.item_name, item_sku: item?.item_sku ?? '',
      mov_type: 'Purchase', quantity: qty, reference: this.addStockForm.reference,
      mov_date: now, balance_after: newBal, notes: this.addStockForm.notes
    };
    this.doSave('inv_item_movements', movItem);
    this.movements.update(l => [movItem, ...l]);

    this.showAddStockModal.set(false);
    this.flash(`${qty} units added to ${this.addStockForm.item_name}.`);
  }

  // ── Supplier CRUD ─────────────────────────────────────────
  openSupplierForm(sup?: Supplier): void {
    if (sup) { this.supplierForm = { ...sup }; this.editingId.set(sup.id); }
    else {
      this.supplierForm = { sup_name: '', contact_person: '', email: '', phone: '', address: '', city: '', gst_no: '', payment_terms: '30 days', is_active: true };
      this.editingId.set(null);
    }
    this.supSubmitted = false;
    this.showSupplierModal.set(true);
  }

  saveSupplier(): void {
    this.supSubmitted = true;
    if (!this.supplierForm.sup_name?.trim()) return;
    const id = this.editingId();
    if (id) {
      this.doUpdate('inv_suppliers', this.supplierForm);
      this.suppliers.update(l => l.map(s => s.id === id ? { ...s, ...this.supplierForm } as Supplier : s));
    } else {
      this.doSave('inv_suppliers', this.supplierForm);
      const n = ++this.supCounter;
      this.suppliers.update(l => [...l, { id: n, sup_name: this.supplierForm.sup_name!, contact_person: this.supplierForm.contact_person ?? '', email: this.supplierForm.email ?? '', phone: this.supplierForm.phone ?? '', address: this.supplierForm.address ?? '', city: this.supplierForm.city ?? '', gst_no: this.supplierForm.gst_no ?? '', payment_terms: this.supplierForm.payment_terms ?? '30 days', is_active: true }]);
    }
    this.showSupplierModal.set(false);
    this.flash('Supplier saved.');
  }

  toggleSupplier(id: number): void {
    this.suppliers.update(l => l.map(s => s.id === id ? { ...s, is_active: !s.is_active } : s));

    this.suppliers().filter((obj: any)=>{
      if(obj.id === id) {
        return this.doUpdate('inv_suppliers', obj);
      }
    });
  }

  // ── PO CRUD ───────────────────────────────────────────────
  openPOForm(): void {
    this.poForm = {
      supplier_id: null, supplier_name: '', order_date: this.isoDate(0),
      expected_date: this.isoDate(-7), notes: '',
      items: [{ item_id: 0, item_name: '', item_sku: '', unit: 'Pcs', quantity: 0, received_qty: 0, unit_price: 0, total: 0 }],
    };
    this.editingId.set(null);
    this.poSubmitted = false;
    this.showPOModal.set(true);
  }

  addPOLine(): void {
    this.poForm.items.push({ item_id: 0, item_name: '', item_sku: '', unit: 'Pcs', quantity: 0, received_qty: 0, unit_price: 0, total: 0 });
  }
  removePOLine(i: number): void { if (this.poForm.items.length > 1) this.poForm.items.splice(i, 1); }
  updatePOLineTotal(line: Partial<POItem>): void { line.total = (line.quantity ?? 0) * (line.unit_price ?? 0); }
  poTotal(): number { return this.poForm.items.reduce((s, l) => s + (l.total ?? 0), 0); }

  savePO(): void {
    this.poSubmitted = true;
    if (!this.poForm.supplier_name?.trim() || !this.poForm.order_date) return;

    const n = ++this.poCounter;
    const numbers = '0123456789';
    const customCode = this.dataService.getUUID(5, numbers);

    const po: PurchaseOrder = {
      id: n, po_no: 'PO-2026-'+customCode,
      supplier_id: this.poForm.supplier_id ?? 0, supplier_name: this.poForm.supplier_name,
      order_date: this.poForm.order_date, expected_date: this.poForm.expected_date,
      received_date: '', items: this.poForm.items as POItem[],
      total_amount: this.poTotal(), status: 'Draft', notes: this.poForm.notes,
      created_by: this.user.id, created_at: new Date().toISOString(),
    };

    let frmPO: any = Object.assign({}, po);
    frmPO.items = JSON.stringify(frmPO.items) as any;
    frmPO.created_by = this.user.id;
    frmPO.created_at = this.isoDate(0);    // || frmPO.order_date;
    frmPO.received_date = null;
    delete frmPO.id;

    this.doSave('inv_purchase_orders', frmPO);

    this.purchaseOrders.update(l => [po, ...l]);
    this.showPOModal.set(false);
    this.flash(`PO ${po.po_no} created.`);
  }

  sendPO(po: any): void {
    const id: number = po.id;
    this.dataService.updateRecord('inv_purord_status', id, { status: 'Sent' }).subscribe();

    this.purchaseOrders.update(l => l.map(p => p.id === id ? { ...p, status: 'Sent' } : p));
    this.flash('PO sent to supplier.');
  }

  receivePO(id: number): void {
    const po = this.purchaseOrders().find(p => p.id === id);
    if (!po) return;
    const now = this.isoDate(0);

    // Update stock
    this.items.update(items => items.map(item => {
      const line = po.items.find(l => l.item_id === item.id);
      return line ? { ...item, current_stock: item.current_stock + line.quantity, last_purchase_date: now } : item;
    }));

    // Record movements
    this.movements.update(mv => [
      ...po.items.map(l => ({
        id: ++this.movCounter, item_id: l.item_id, item_name: l.item_name, item_sku: l.item_sku,
        mov_type: 'Purchase' as MovementType, quantity: l.quantity, reference: po.po_no,
        mov_date: now, balance_after: (this.items().find(i => i.id === l.item_id)?.current_stock ?? 0), notes: `Received – ${po.po_no}`,
      })),
      ...mv,
    ]);

    this.doSave('inv_item_movements', po);
    this.dataService.updateRecord('inv_purord_status', id, { status: 'Received', received_date: this.isoDate(0) }).subscribe();

    this.purchaseOrders.update(l => l.map(p => p.id === id ? { ...p, status: 'Received', received_date: now } : p));
    this.flash(`PO ${po.po_no} received. Stock updated.`);
  }

  cancelPO(id: number): void {
    if (!confirm('Cancel this purchase order?')) return;
    this.purchaseOrders.update(l => l.map(p => p.id === id ? { ...p, status: 'Cancelled' } : p));
    this.flash('PO cancelled.');
  }

  // ── Issue CRUD ────────────────────────────────────────────
  openIssueForm(): void {
    this.issueForm = {
      department: '', requested_by: '', purpose: '', remarks: '',
      items: [{ item_id: 0, item_name: '', item_sku: '', unit: 'Pcs', requested_qty: 0, issued_qty: 0 }],
    };
    this.issSubmitted = false;
    this.showIssueModal.set(true);
  }

  addIssueLine(): void {
    this.issueForm.items.push({ item_id: 0, item_name: '', item_sku: '', unit: 'Pcs', requested_qty: 0, issued_qty: 0 });
  }
  removeIssueLine(i: number): void { if (this.issueForm.items.length > 1) this.issueForm.items.splice(i, 1); }

  saveIssueRequest(): void {
    this.issSubmitted = true;
    if (!this.issueForm.department?.trim() || !this.issueForm.requested_by?.trim()) return;

    const n = ++this.issCounter;
    const numbers = '0123456789';
    const customCode = this.dataService.getUUID(5, numbers);
    const iss: StockIssue = {
      id: n, issue_no: 'ISS-2026-'+customCode,
      department: this.issueForm.department, requested_by: this.issueForm.requested_by,
      request_date: this.isoDate(0), issue_date: '', items: this.issueForm.items as IssueItem[],
      status: 'Pending', purpose: this.issueForm.purpose,
      approved_by: '', remarks: this.issueForm.remarks,
    };

    let frmIss: any = Object.assign({}, iss);
    frmIss.items = JSON.stringify(frmIss.items) as any;
    frmIss.approved_by = this.user.id;
    frmIss.issue_date = this.isoDate(0);
    delete frmIss.id;

    this.doSave('inv_stock_issues', frmIss);

    this.stockIssues.update(l => [iss, ...l]);
    this.showIssueModal.set(false);
    this.flash(`Issue request ${iss.issue_no} submitted.`);
  }

  approveIssue(id: number): void {
    this.dataService.updateRecord('inv_stock_status', id, { status: 'Approved' }).subscribe();

    this.stockIssues.update(l => l.map(i => i.id === id ? { ...i, status: 'Approved' } : i));
    this.flash('Issue request approved.');
  }

  issueStock(id: number): void {
    const iss = this.stockIssues().find(i => i.id === id);
    if (!iss) return;
    const now = this.isoDate(0);
    // Deduct stock
    this.items.update(items => items.map(item => {
      const line = iss.items.find(l => l.item_id === item.id);
      // return line ? { ...item, current_stock: Math.max(0, item.current_stock - line.requested_qty) } : item;

      if(line) {
        item = { ...item, current_stock: Math.max(0, item.current_stock - line.requested_qty) };
        this.doUpdate('inv_items', item);

        return item;
      } else {
        return item;
      }
    }));

    // Record movements
    /*this.movements.update(mv => [
      ...iss.items.map(l => ({
        id: ++this.movCounter, item_id: l.item_id, item_name: l.item_name, item_sku: l.item_sku,
        mov_type: 'Issue' as MovementType, quantity: -l.requested_qty, reference: iss.issue_no,
        mov_date: now, balance_after: (this.items().find(i => i.id === l.item_id)?.current_stock ?? 0),
        notes: `Issued to ${iss.department}`,
      })),
      ...mv,
    ]);*/
    // 1. Extract and map the internal data object
    const newMovements = iss.items.map(l => ({
      id: ++this.movCounter,
      item_id: l.item_id,
      item_name: l.item_name,
      item_sku: l.item_sku,
      mov_type: 'Issue' as MovementType,
      quantity: -l.requested_qty,
      reference: iss.issue_no,
      mov_date: now,
      balance_after: (this.items().find(i => i.id === l.item_id)?.current_stock ?? 0),
      notes: `Issued to ${iss.department}`,
    }));

    // 2. Save the data object "as is" (e.g., API call or local storage)
    this.doSave('inv_item_movements', newMovements.length ? newMovements[0] : newMovements);
    this.dataService.updateRecord('inv_stock_status', id, { status: 'Issued', received_date: this.isoDate(0) }).subscribe();

    // 3. Update your reactive state
    this.movements.update(mv => [...newMovements, ...mv]);
    this.stockIssues.update(l => l.map(i => i.id === id ? { ...i, status: 'Issued', issue_date: now } : i));
    this.flash(`Stock issued to ${iss.department}.`);
  }

  rejectIssue(id: number): void {
    if (!confirm('Reject this issue request?')) return;
    this.stockIssues.update(l => l.map(i => i.id === id ? { ...i, status: 'Rejected' } : i));
    this.flash('Issue request rejected.');
  }

  // ── Helpers ───────────────────────────────────────────────
  stockStatus(i: InvItem): StockStatus {
    if (i.current_stock === 0) return 'Out of Stock';
    if (i.current_stock < i.min_stock) return 'Low Stock';
    return 'In Stock';
  }
  getStockStatusClass(s: StockStatus): string {
    return { 'In Stock': 'st-instock', 'Low Stock': 'st-lowstock', 'Out of Stock': 'st-outstock' }[s] ?? '';
  }
  getPOStatusClass(s: POStatus): string {
    return { Draft: 'st-draft', Sent: 'st-sent', Partial: 'st-partial', Received: 'st-received', Cancelled: 'st-cancelled' }[s] ?? '';
  }
  getIssStatusClass(s: IssueStatus): string {
    return { Pending: 'st-ipending', Approved: 'st-iapproved', Issued: 'st-iissued', Rejected: 'st-irejected' }[s] ?? '';
  }
  getCatColor(cat: string): string {
    const catName: any = this.dataService.getInvCat(cat);
    const m: Record<string, string> = { Stationery: '#3b82f6', 'Lab Equipment': '#7c3aed', 'IT Equipment': '#0891b2', Furniture: '#d97706', Sports: '#059669', Library: '#dc2626', Maintenance: '#6b7280', Canteen: '#f59e0b', Others: '#9ca3af' };
    return m[catName] ?? '#6b7280';
  }
  getCatBg(cat: string): string {
    const catName: any = this.dataService.getInvCat(cat);
    const m: Record<string, string> = { Stationery: '#dbeafe', 'Lab Equipment': '#ede9fe', 'IT Equipment': '#cffafe', Furniture: '#fef3c7', Sports: '#dcfce7', Library: '#fee2e2', Maintenance: '#f3f4f6', Canteen: '#fef9c3', Others: '#f3f4f6' };
    return m[catName] ?? '#f3f4f6';
  }
  getMovTypeIcon(t: MovementType): string {
    return { Purchase: 'bi-arrow-down-circle-fill', Issue: 'bi-arrow-up-circle-fill', Return: 'bi-arrow-clockwise', Adjustment: 'bi-sliders', Opening: 'bi-box-seam-fill' }[t] ?? 'bi-circle';
  }
  stockPct(i: InvItem): number { return i.max_stock > 0 ? Math.min(100, Math.round((i.current_stock / i.max_stock) * 100)) : 0; }

  formatCurrency(n: number): string {
    if (n >= 10000000) return '₹' + (n / 10000000).toFixed(2) + 'Cr';
    if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + 'L';
    if (n >= 1000) return '₹' + (n / 1000).toFixed(1) + 'K';
    return '₹' + n.toFixed(0);
  }
  formatFull(n: number): string {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
  }
  formatDate(s: string): string { if (!s) return '—'; return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  formatDateTime(s: string): string { if (!s) return '—'; return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  private flash(m: string): void { this.toast.set(m); setTimeout(() => this.toast.set(''), 3000); }
  private isoDate(offset: number): string { const d = new Date(); d.setDate(d.getDate() - offset); return d.toISOString().substring(0, 10); }

  // ──────────────────────────────────────────────────────────
  // SEED DATA
  // ──────────────────────────────────────────────────────────
  // private buildItems(): InvItem[] {
  //   const it = (id: number, sku: string, item_name: string, cat: ItemCategory, unit: ItemUnit, cur: number, min: number, max: number, cost: number, supId: number, supName: string, loc: string, desc: string, lpd: string): InvItem =>
  //     ({ id, sku, item_name, category: cat, unit, current_stock: cur, min_stock: min, max_stock: max, cost_price: cost, supplier_id: supId, supplier_name: supName, storage_location: loc, is_active: true, description: desc, last_purchase_date: lpd });
  //   return [
  //     it(1, 'STN-001', 'A4 Paper 80gsm', 'Stationery', 'Ream', 148, 50, 500, 350, 1, 'Sharma Stationery Mart', 'Store-A1', '500-sheet ream', this.isoDate(20)),
  //     it(2, 'STN-002', 'Ball Pens (Blue, Box/50)', 'Stationery', 'Box', 42, 20, 100, 180, 1, 'Sharma Stationery Mart', 'Store-A2', 'Box of 50 pens', this.isoDate(20)),
  //     it(3, 'STN-003', 'Whiteboard Marker Set', 'Stationery', 'Pack', 18, 15, 80, 220, 1, 'Sharma Stationery Mart', 'Store-A2', '4-colour pack', this.isoDate(15)),
  //     it(4, 'STN-004', 'A4 Notebooks 200-page', 'Stationery', 'Pcs', 210, 50, 500, 65, 1, 'Sharma Stationery Mart', 'Store-A3', 'Single-line', this.isoDate(20)),
  //     it(5, 'STN-005', 'Stapler', 'Stationery', 'Pcs', 6, 10, 30, 250, 1, 'Sharma Stationery Mart', 'Store-A4', 'Heavy-duty', this.isoDate(60)),
  //     it(6, 'STN-006', 'Printer Ink Cartridge', 'Stationery', 'Pcs', 4, 10, 50, 850, 1, 'Sharma Stationery Mart', 'Store-A4', 'HP 680 Black', this.isoDate(30)),
  //     it(7, 'LAB-001', 'Glass Test Tubes', 'Lab Equipment', 'Pcs', 480, 100, 1000, 12, 2, 'LabTech Scientific', 'Lab-B1', 'Borosilicate', this.isoDate(45)),
  //     it(8, 'LAB-002', 'Beakers 250ml', 'Lab Equipment', 'Pcs', 22, 20, 100, 85, 2, 'LabTech Scientific', 'Lab-B1', 'Borosilicate', this.isoDate(45)),
  //     it(9, 'LAB-003', 'Compound Microscope', 'Lab Equipment', 'Pcs', 12, 5, 20, 8500, 2, 'LabTech Scientific', 'Lab-B2', '40x–1000x', this.isoDate(180)),
  //     it(10, 'LAB-004', 'Sodium Chloride (NaCl)', 'Lab Equipment', 'Kg', 8, 10, 50, 95, 2, 'LabTech Scientific', 'Lab-C1', 'AR Grade', this.isoDate(30)),
  //     it(11, 'LAB-005', 'Bunsen Burner', 'Lab Equipment', 'Pcs', 14, 5, 25, 1200, 2, 'LabTech Scientific', 'Lab-B2', 'LPG type', this.isoDate(90)),
  //     it(12, 'IT-001', 'HP Laptop 15.6"', 'IT Equipment', 'Pcs', 44, 10, 60, 45000, 3, 'TechPro Solutions', 'IT-Room1', 'Intel i5 8GB', this.isoDate(60)),
  //     it(13, 'IT-002', 'Multimedia Projector', 'IT Equipment', 'Pcs', 7, 5, 20, 38000, 3, 'TechPro Solutions', 'IT-Room2', '3500 lumens', this.isoDate(90)),
  //     it(14, 'IT-003', 'USB Flash Drive 32GB', 'IT Equipment', 'Pcs', 46, 20, 100, 380, 3, 'TechPro Solutions', 'IT-Store', 'USB 3.0', this.isoDate(15)),
  //     it(15, 'IT-004', 'HDMI Cable 1.8m', 'IT Equipment', 'Pcs', 25, 10, 50, 220, 3, 'TechPro Solutions', 'IT-Store', '4K ready', this.isoDate(15)),
  //     it(16, 'FRN-001', 'Student Desk & Chair', 'Furniture', 'Set', 118, 20, 200, 2800, 4, 'FurniMart India', 'Warehouse', 'Wooden top', this.isoDate(120)),
  //     it(17, 'FRN-002', 'Teacher Chair', 'Furniture', 'Pcs', 28, 10, 50, 1500, 4, 'FurniMart India', 'Warehouse', 'High-back', this.isoDate(120)),
  //     it(18, 'SPT-001', 'Football (Size 5)', 'Sports', 'Pcs', 18, 10, 40, 850, 5, 'SportZone', 'Sports-S1', 'FIFA approved', this.isoDate(30)),
  //     it(19, 'SPT-002', 'Basketball', 'Sports', 'Pcs', 9, 5, 20, 1100, 5, 'SportZone', 'Sports-S1', 'FIBA standard', this.isoDate(30)),
  //     it(20, 'SPT-003', 'Cricket Kit Complete', 'Sports', 'Set', 3, 3, 10, 8500, 5, 'SportZone', 'Sports-S2', 'Full kit', this.isoDate(90)),
  //     it(21, 'LIB-001', 'Reference Books – Sci', 'Library', 'Pcs', 780, 100, 1200, 450, 1, 'Sharma Stationery Mart', 'Library', 'NCERT & others', this.isoDate(30)),
  //     it(22, 'MNT-001', 'Floor Cleaner (1Ltr)', 'Maintenance', 'Ltr', 48, 20, 100, 185, 1, 'Sharma Stationery Mart', 'Store-M1', 'Multi-purpose', this.isoDate(15)),
  //     it(23, 'MNT-002', 'Garbage Bags (Roll)', 'Maintenance', 'Roll', 22, 10, 50, 120, 1, 'Sharma Stationery Mart', 'Store-M1', '50 bags/roll', this.isoDate(15)),
  //     it(24, 'STN-007', 'Chalk Box', 'Stationery', 'Box', 0, 20, 100, 45, 1, 'Sharma Stationery Mart', 'Store-A1', 'White, 100 pcs', this.isoDate(60)),
  //     it(25, 'LAB-006', 'Filter Paper (Pack)', 'Lab Equipment', 'Pack', 3, 10, 50, 320, 2, 'LabTech Scientific', 'Lab-C1', 'Whatman Grade 1', this.isoDate(60)),
  //   ];
  // }

  // private buildSuppliers(): Supplier[] {
  //   const s = (id: number, sup_name: string, cp: string, email: string, phone: string, addr: string, city: string, gst: string, terms: string): Supplier =>
  //     ({ id, sup_name, contact_person: cp, email, phone, address: addr, city, gst_no: gst, payment_terms: terms, is_active: true });
  //   return [
  //     s(1, 'Sharma Stationery Mart', 'Suresh Sharma', 'suresh@sharmamart.com', '9876543210', '12, MG Road, Connaught Place', 'Delhi', '07AABCS1234A1Z5', '30 days'),
  //     s(2, 'LabTech Scientific Pvt Ltd', 'Dr Ranjit Singh', 'ranjit@labtech.in', '9845678901', 'Plot 42, MIDC Industrial Area', 'Pune', '27AALCL5678B1Z2', '45 days'),
  //     s(3, 'TechPro Solutions', 'Ashish Gupta', 'ashish@techpro.co.in', '9823456789', '56, IT Park, Whitefield', 'Bengaluru', '29AADCT9012C1Z9', '30 days'),
  //     s(4, 'FurniMart India', 'Mohan Verma', 'mohan@furnimart.in', '9812345678', '78, Industrial Estate', 'Jaipur', '08AABCF3456D1Z1', '60 days'),
  //     s(5, 'SportZone Pvt Ltd', 'Arjun Pillai', 'arjun@sportzone.in', '9867890123', '23, Stadium Road', 'Chennai', '33AADCS7890E1Z6', '30 days'),
  //   ];
  // }

  // private buildPOs(): PurchaseOrder[] {
  //   const mk = (id: number, supId: number, supName: string, ordOff: number, expOff: number, recOff: number | null, items: POItem[], st: POStatus, notes: string): PurchaseOrder => {
  //     const total = items.reduce((s, i) => s + i.total, 0);
  //     return { id, po_no: `PO-2026-${String(id).padStart(4, '0')}`, supplier_id: supId, supplier_name: supName, order_date: this.isoDate(ordOff), expected_date: this.isoDate(expOff), received_date: recOff !== null ? this.isoDate(recOff) : '', items, total_amount: total, status: st, notes, created_by: 'Admin User', created_at: new Date(Date.now() - ordOff * 86400000).toISOString() };
  //   };
  //   const pi = (iid: number, name: string, sku: string, unit: string, qty: number, rqty: number, up: number): POItem => ({ item_id: iid, item_name: name, sku, unit, quantity: qty, received_qty: rqty, unit_price: up, total: qty * up });
  //   return [
  //     mk(1, 1, 'Sharma Stationery Mart', 20, 15, 14,
  //       [pi(1, 'A4 Paper 80gsm', 'STN-001', 'Ream', 100, 100, 350), 
  //         pi(2, 'Ball Pens', 'STN-002', 'Box', 20, 20, 180), 
  //         pi(4, 'A4 Notebooks', 'STN-004', 'Pcs', 200, 200, 65)],
  //       'Received', 'Monthly stationery restock'),
  //     mk(2, 2, 'LabTech Scientific Pvt Ltd', 15, 10, null,
  //       [pi(7, 'Glass Test Tubes', 'LAB-001', 'Pcs', 200, 100, 12), pi(8, 'Beakers 250ml', 'LAB-002', 'Pcs', 10, 5, 85), pi(10, 'Sodium Chloride', 'LAB-004', 'Kg', 20, 10, 95)],
  //       'Partial', 'Lab supplies Q2 2026'),
  //     mk(3, 3, 'TechPro Solutions', 10, 5, 3,
  //       [pi(14, 'USB Flash Drive', 'IT-003', 'Pcs', 50, 50, 380), pi(15, 'HDMI Cable', 'IT-004', 'Pcs', 20, 20, 220)],
  //       'Received', 'IT accessories'),
  //     mk(4, 4, 'FurniMart India', 5, -5, null,
  //       [pi(16, 'Student Desk & Chair', 'FRN-001', 'Set', 20, 0, 2800), pi(17, 'Teacher Chair', 'FRN-002', 'Pcs', 5, 0, 1500)],
  //       'Sent', 'Annual furniture procurement'),
  //     mk(5, 5, 'SportZone Pvt Ltd', 2, -3, null,
  //       [pi(18, 'Football', 'SPT-001', 'Pcs', 10, 0, 850), pi(19, 'Basketball', 'SPT-002', 'Pcs', 5, 0, 1100)],
  //       'Draft', 'Sports day preparations'),
  //     mk(6, 1, 'Sharma Stationery Mart', 30, 25, 22,
  //       [pi(21, 'Reference Books', 'LIB-001', 'Pcs', 100, 100, 450), pi(22, 'Floor Cleaner', 'MNT-001', 'Ltr', 30, 30, 185)],
  //       'Received', 'Library and maintenance'),
  //   ];
  // }

  // private buildIssues(): StockIssue[] {
  //   const mk = (id: number, dept: string, reqBy: string, reqOff: number, issOff: number | null, items: IssueItem[], st: IssueStatus, purpose: string, by: string): StockIssue => ({
  //     id, issue_no: `ISS-2026-${String(id).padStart(4, '0')}`,
  //     department: dept, requested_by: reqBy, request_date: this.isoDate(reqOff),
  //     issue_date: issOff !== null ? this.isoDate(issOff) : '',
  //     items, status: st, purpose, approved_by: by, remarks: '',
  //   });
  //   const ii = (iid: number, name: string, sku: string, unit: string, rq: number): IssueItem => ({ item_id: iid, item_name: name, sku, unit, requested_qty: rq, issued_qty: rq });
  //   return [
  //     mk(1, 'Science Department', 'Dr. Kavya Menon', 5, 4, [ii(7, 'Test Tubes', 'LAB-001', 'Pcs', 50), ii(8, 'Beakers', 'LAB-002', 'Pcs', 5), ii(10, 'Sodium Chloride', 'LAB-004', 'Kg', 2)], 'Issued', 'Practical experiments – Class XI', 'Admin User'),
  //     mk(2, 'IT Lab', 'Mr. Suresh Patel', 3, 2, [ii(14, 'USB Flash Drive', 'IT-003', 'Pcs', 10), ii(15, 'HDMI Cable', 'IT-004', 'Pcs', 4)], 'Issued', 'Computer lab upgrades', 'Admin User'),
  //     mk(3, 'Administration', 'Mrs. Leela Nair', 2, null, [ii(1, 'A4 Paper', 'STN-001', 'Ream', 5), ii(2, 'Ball Pens', 'STN-002', 'Box', 3), ii(3, 'WB Markers', 'STN-003', 'Pack', 4)], 'Approved', 'Office stationery replenishment', 'Admin User'),
  //     mk(4, 'Sports Department', 'Coach Rajan', 1, null, [ii(18, 'Football', 'SPT-001', 'Pcs', 3)], 'Pending', 'Practice session', ''),
  //     mk(5, 'Library', 'Mrs. Suja George', 0, null, [ii(21, 'Reference Books', 'LIB-001', 'Pcs', 20)], 'Pending', 'New arrivals cataloguing', ''),
  //   ];
  // }

  // private buildMovements(): StockMovement[] {
  //   const mv = (id: number, iid: number, name: string, sku: string, type: MovementType, qty: number, ref: string, dOff: number, bal: number, notes: string): StockMovement =>
  //     ({ id, item_id: iid, item_name: name, sku, type, quantity: qty, reference: ref, date: this.isoDate(dOff), balance_after: bal, notes });
  //   return [
  //     mv(1, 1, 'A4 PaperZ 80gsm', 'STN-001', 'Purchase', 100, 'PO-2026-0001', 20, 200, 'Stock received PO-2026-0001'),
  //     mv(2, 2, 'Ball Pens', 'STN-002', 'Purchase', 20, 'PO-2026-0001', 20, 45, 'Stock received PO-2026-0001'),
  //     mv(3, 4, 'A4 Notebooks', 'STN-004', 'Purchase', 200, 'PO-2026-0001', 20, 210, 'Stock received PO-2026-0001'),
  //     mv(4, 7, 'Glass Test Tubes', 'LAB-001', 'Purchase', 100, 'PO-2026-0002', 15, 500, 'Partial receipt PO-2026-0002'),
  //     mv(5, 8, 'Beakers 250ml', 'LAB-002', 'Purchase', 5, 'PO-2026-0002', 15, 22, 'Partial receipt PO-2026-0002'),
  //     mv(6, 14, 'USB Flash Drive', 'IT-003', 'Purchase', 50, 'PO-2026-0003', 10, 50, 'Stock received PO-2026-0003'),
  //     mv(7, 15, 'HDMI Cable', 'IT-004', 'Purchase', 20, 'PO-2026-0003', 10, 25, 'Stock received PO-2026-0003'),
  //     mv(8, 7, 'Glass Test Tubes', 'LAB-001', 'Issue', -50, 'ISS-2026-0001', 5, 480, 'Issued to Science Dept'),
  //     mv(9, 8, 'Beakers 250ml', 'LAB-002', 'Issue', -5, 'ISS-2026-0001', 4, 22, 'Issued to Science Dept'),
  //     mv(10, 14, 'USB Flash Drive', 'IT-003', 'Issue', -10, 'ISS-2026-0002', 3, 46, 'Issued to IT Lab'),
  //     mv(11, 15, 'HDMI Cable', 'IT-004', 'Issue', -4, 'ISS-2026-0002', 2, 25, 'Issued to IT Lab'),
  //     mv(12, 21, 'Reference Books', 'LIB-001', 'Purchase', 100, 'PO-2026-0006', 30, 780, 'Stock received PO-2026-0006'),
  //     mv(13, 22, 'Floor Cleaner', 'MNT-001', 'Purchase', 30, 'PO-2026-0006', 22, 48, 'Stock received PO-2026-0006'),
  //     mv(14, 1, 'A4 Paper 80gsm', 'STN-001', 'Issue', -5, 'ISS-2026-0003', 2, 148, 'Issued to Admin'),
  //     mv(15, 9, 'Compound Microscope', 'LAB-003', 'Opening', 12, 'OPEN-2026', 180, 12, 'Opening stock entry'),
  //     mv(16, 12, 'HP Laptop 15.6"', 'IT-001', 'Opening', 44, 'OPEN-2026', 180, 44, 'Opening stock entry'),
  //     mv(17, 16, 'Student Desk & Chair', 'FRN-001', 'Opening', 120, 'OPEN-2026', 180, 120, 'Opening stock entry'),
  //   ];
  // }


  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
